// components/wizard/Reviewers.jsx
import React, { useState } from 'react';
import { Plus, X, GripVertical, Loader } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { showToast } from '../../../utils/toast';

const Reviewers = ({ formData, updateFormData, onNext, onBack, saving }) => {
  const [newReviewer, setNewReviewer] = useState({
    fullName: '',
    affiliation: '',
    city: '',
    country: '',
    email: ''
  });

  const addReviewer = () => {
    if (formData.reviewers.length >= 6) {
      showToast('Maximum 6 reviewers allowed', 'error');
      return;
    }

    updateFormData({
      reviewers: [...formData.reviewers, newReviewer]
    });
    setNewReviewer({
      fullName: '',
      affiliation: '',
      city: '',
      country: '',
      email: ''
    });
  };

  const removeReviewer = (index) => {
    const newReviewers = formData.reviewers.filter((_, i) => i !== index);
    updateFormData({ reviewers: newReviewers });
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(formData.reviewers);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    updateFormData({ reviewers: items });
  };

  return (
    <div>
      <h3 className="text-xl font-semibold mb-6">Suggest Reviewers (Optional)</h3>

      {/* Add New Reviewer Form */}
      <div className="bg-gray-50 p-6 rounded-lg mb-6">
        <h4 className="font-medium mb-4">Add New Reviewer</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <input
              type="text"
              value={newReviewer.fullName}
              onChange={(e) => setNewReviewer({ ...newReviewer, fullName: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg"
              placeholder="Dr. Mandela Mbadiwe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Affiliation
            </label>
            <input
              type="text"
              value={newReviewer.affiliation}
              onChange={(e) => setNewReviewer({ ...newReviewer, affiliation: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg"
              placeholder="University of Science"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City
            </label>
            <input
              type="text"
              value={newReviewer.city}
              onChange={(e) => setNewReviewer({ ...newReviewer, city: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg"
              placeholder="Nairobi"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Country
            </label>
            <input
              type="text"
              value={newReviewer.country}
              onChange={(e) => setNewReviewer({ ...newReviewer, country: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg"
              placeholder="Kenya"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={newReviewer.email}
              onChange={(e) => setNewReviewer({ ...newReviewer, email: e.target.value })}
              className="w-full p-2 border border-gray-300 rounded-lg"
              placeholder="mandela.mbadiwe@university.edu"
            />
          </div>
        </div>
        <button
          onClick={addReviewer}
          disabled={!newReviewer.fullName || !newReviewer.email}
          className="mt-4 px-4 py-2 bg-[--asfi-blue] text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-300 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Reviewer
        </button>
      </div>

      {/* Reviewers List */}
      {formData.reviewers.length > 0 && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="reviewers">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-4"
              >
                {formData.reviewers.map((reviewer, index) => (
                  <Draggable key={index} draggableId={`reviewer-${index}`} index={index}>
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className="bg-white border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-start">
                          <div
                            {...provided.dragHandleProps}
                            className="mr-3 mt-1 cursor-move"
                          >
                            <GripVertical className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs text-gray-500">Name</label>
                                <p className="text-sm font-medium">{reviewer.fullName}</p>
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500">Email</label>
                                <p className="text-sm">{reviewer.email}</p>
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500">Affiliation</label>
                                <p className="text-sm">{reviewer.affiliation}</p>
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500">Location</label>
                                <p className="text-sm">{reviewer.city}, {reviewer.country}</p>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeReviewer(index)}
                            className="p-1 text-red-600 hover:bg-red-50 rounded"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
        >
          Back
        </button>
        
        <button
          onClick={onNext}
          disabled={saving}
          className="px-8 py-3 bg-[--asfi-blue] text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          {saving ? (
            <>
              <Loader className="w-4 h-4 animate-spin inline mr-2" />
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </button>
      </div>
    </div>
  );
};

export default Reviewers;
